import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';

export interface ReportColumnConfig {
  header: string;
  key: string;
  width?: number;
  align?: 'left' | 'center' | 'right';
  numFmt?: string; // e.g. '#,##0', '#,##0 "₫"', 'dd/mm/yyyy'
}

export interface BaseReportParams {
  title: string;
  periodText?: string;
  creatorName?: string;
  filename: string;
  sheetName?: string;
}

/**
 * Format currency VND for text display
 */
export const formatCurrencyVND = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(amount);
};

/**
 * Common helper to style and build enterprise-grade Excel sheets
 */
async function buildAndDownloadWorkbook(params: {
  base: BaseReportParams;
  columns: ReportColumnConfig[];
  data: any[];
  summaryRow?: Record<string, any>;
}) {
  const { base, columns, data, summaryRow } = params;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = base.creatorName || 'APV ERP System';
  workbook.created = new Date();

  const worksheet = workbook.addWorksheet(base.sheetName || 'Báo Cáo', {
    views: [{ showGridLines: true }]
  });

  const totalCols = columns.length;

  // 1. Company Header
  const companyTitleRow = worksheet.addRow(['CÔNG TY CỔ PHẦN BAO BÌ APV - HỆ THỐNG ERP QUẢN TRỊ SẢN XUẤT & TÀI CHÍNH']);
  companyTitleRow.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FF1E293B' } };
  worksheet.mergeCells(1, 1, 1, Math.min(totalCols, 4));

  const companySubRow = worksheet.addRow(['Địa chỉ: Lô CN-08, KCN Yên Phong, Bắc Ninh | Hotline: 098.123.4567 | MST: 2300987654']);
  companySubRow.font = { name: 'Arial', size: 9, italic: true, color: { argb: 'FF64748B' } };
  worksheet.mergeCells(2, 1, 2, Math.min(totalCols, 4));

  // Blank row
  worksheet.addRow([]);

  // 2. Report Main Title
  const titleRow = worksheet.addRow([base.title.toUpperCase()]);
  titleRow.font = { name: 'Arial', size: 15, bold: true, color: { argb: 'FF0F172A' } };
  titleRow.alignment = { horizontal: 'center', vertical: 'middle' };
  worksheet.mergeCells(4, 1, 4, totalCols);
  titleRow.height = 28;

  // Subtitle / Period
  const period = base.periodText || `Thời gian: Ngày ${format(new Date(), 'dd/MM/yyyy')}`;
  const subtitleRow = worksheet.addRow([period]);
  subtitleRow.font = { name: 'Arial', size: 10, italic: true, color: { argb: 'FF475569' } };
  subtitleRow.alignment = { horizontal: 'center', vertical: 'middle' };
  worksheet.mergeCells(5, 1, 5, totalCols);

  // Metadata / Export info
  const metaRow = worksheet.addRow([`Ngày xuất: ${format(new Date(), 'dd/MM/yyyy HH:mm')} | Người lập: ${base.creatorName || 'Ban Quản Trị'}`]);
  metaRow.font = { name: 'Arial', size: 9, color: { argb: 'FF94A3B8' } };
  metaRow.alignment = { horizontal: 'right', vertical: 'middle' };
  worksheet.mergeCells(6, 1, 6, totalCols);

  // Blank line before table
  worksheet.addRow([]);

  // 3. Table Headers (Row 8)
  const headerValues = columns.map(c => c.header);
  const tableHeaderRow = worksheet.addRow(headerValues);
  tableHeaderRow.height = 26;
  tableHeaderRow.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
  tableHeaderRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };

  // Style header cells
  for (let colIdx = 1; colIdx <= totalCols; colIdx++) {
    const cell = tableHeaderRow.getCell(colIdx);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF1E293B' } // Slate-800 Navy
    };
    cell.border = {
      top: { style: 'medium', color: { argb: 'FF0F172A' } },
      left: { style: 'thin', color: { argb: 'FF334155' } },
      bottom: { style: 'medium', color: { argb: 'FF0F172A' } },
      right: { style: 'thin', color: { argb: 'FF334155' } },
    };
  }

  // 4. Data Rows
  data.forEach((rowItem, rIdx) => {
    const rowValues = columns.map(col => {
      let val = rowItem[col.key];
      if (col.key.includes('.')) {
        val = col.key.split('.').reduce((obj, k) => (obj ? obj[k] : undefined), rowItem);
      }
      return val !== undefined && val !== null ? val : '';
    });

    const dataRow = worksheet.addRow(rowValues);
    dataRow.height = 20;

    const isEven = rIdx % 2 === 0;
    const bgArgb = isEven ? 'FFFFFFFF' : 'FFF8FAFC'; // slate-50

    columns.forEach((col, cIdx) => {
      const cell = dataRow.getCell(cIdx + 1);
      cell.font = { name: 'Arial', size: 9.5 };
      cell.alignment = {
        vertical: 'middle',
        horizontal: col.align || (typeof rowValues[cIdx] === 'number' ? 'right' : 'left'),
        wrapText: false
      };

      if (col.numFmt) {
        cell.numFmt = col.numFmt;
      }

      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: bgArgb }
      };

      cell.border = {
        top: { style: 'thin', color: { argb: 'FFE2E8F0' } },
        left: { style: 'thin', color: { argb: 'FFE2E8F0' } },
        bottom: { style: 'thin', color: { argb: 'FFE2E8F0' } },
        right: { style: 'thin', color: { argb: 'FFE2E8F0' } },
      };
    });
  });

  // 5. Summary / Total Row
  if (summaryRow) {
    const summaryValues = columns.map(col => summaryRow[col.key] !== undefined ? summaryRow[col.key] : '');
    const totalRow = worksheet.addRow(summaryValues);
    totalRow.height = 22;
    totalRow.font = { name: 'Arial', size: 10, bold: true, color: { argb: 'FF0F172A' } };

    columns.forEach((col, cIdx) => {
      const cell = totalRow.getCell(cIdx + 1);
      cell.alignment = {
        vertical: 'middle',
        horizontal: col.align || (typeof summaryValues[cIdx] === 'number' ? 'right' : 'left'),
      };
      if (col.numFmt) {
        cell.numFmt = col.numFmt;
      }
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE2E8F0' } // slate-200
      };
      cell.border = {
        top: { style: 'thin', color: { argb: 'FF64748B' } },
        bottom: { style: 'double', color: { argb: 'FF0F172A' } },
        left: { style: 'thin', color: { argb: 'FFCBD5E1' } },
        right: { style: 'thin', color: { argb: 'FFCBD5E1' } },
      };
    });
  }

  // 6. Signature Block
  worksheet.addRow([]);
  worksheet.addRow([]);

  const signTitleRow = worksheet.addRow([]);
  const signSubRow = worksheet.addRow([]);
  signTitleRow.font = { name: 'Arial', size: 10, bold: true };
  signSubRow.font = { name: 'Arial', size: 9, italic: true, color: { argb: 'FF64748B' } };

  const col1 = 2;
  const col2 = Math.floor(totalCols / 2);
  const col3 = totalCols - 1 > 0 ? totalCols - 1 : totalCols;

  signTitleRow.getCell(col1).value = 'NGƯỜI LẬP BIỂU';
  signTitleRow.getCell(col1).alignment = { horizontal: 'center' };
  signSubRow.getCell(col1).value = '(Ký, ghi rõ họ tên)';
  signSubRow.getCell(col1).alignment = { horizontal: 'center' };

  if (totalCols >= 5) {
    signTitleRow.getCell(col2).value = 'KẾ TOÁN TRƯỞNG';
    signTitleRow.getCell(col2).alignment = { horizontal: 'center' };
    signSubRow.getCell(col2).value = '(Ký, ghi rõ họ tên)';
    signSubRow.getCell(col2).alignment = { horizontal: 'center' };
  }

  signTitleRow.getCell(col3).value = 'GIÁM ĐỐC DOANH NGHIỆP';
  signTitleRow.getCell(col3).alignment = { horizontal: 'center' };
  signSubRow.getCell(col3).value = '(Ký, đóng dấu)';
  signSubRow.getCell(col3).alignment = { horizontal: 'center' };

  // Auto-adjust Column Widths with padding
  columns.forEach((col, idx) => {
    const colIndex = idx + 1;
    let maxLen = Math.max(col.header.length, 10);
    
    // Check lengths in data
    data.forEach(item => {
      const val = item[col.key];
      if (val !== undefined && val !== null) {
        const str = String(val);
        if (str.length > maxLen) maxLen = str.length;
      }
    });

    const worksheetColumn = worksheet.getColumn(colIndex);
    worksheetColumn.width = col.width ? col.width : Math.min(Math.max(maxLen + 4, 12), 45);
  });

  // Export buffer & download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, `${base.filename}.xlsx`);
}

/**
 * 1. BÁO CÁO DOANH THU & ĐƠN HÀNG BÁN (Sales & Revenue Report)
 */
export async function exportSalesReport(params: {
  periodText?: string;
  creatorName?: string;
  orders: any[];
  summary?: {
    totalRevenue: number;
    totalOrders: number;
    totalPaid: number;
    totalDebt: number;
  };
}) {
  const { periodText, creatorName, orders, summary } = params;

  const columns: ReportColumnConfig[] = [
    { header: 'STT', key: 'stt', width: 8, align: 'center' },
    { header: 'MÃ ĐƠN HÀNG', key: 'orderCode', width: 18, align: 'center' },
    { header: 'MÃ PO KHÁCH', key: 'poNumber', width: 18, align: 'center' },
    { header: 'TÊN KHÁCH HÀNG', key: 'customerName', width: 32, align: 'left' },
    { header: 'NGÀY ĐẶT HÀNG', key: 'orderDate', width: 14, align: 'center' },
    { header: 'HẠN GIAO HÀNG', key: 'deliveryDate', width: 14, align: 'center' },
    { header: 'TỔNG GIÁ TRỊ (VND)', key: 'totalAmount', width: 22, align: 'right', numFmt: '#,##0 "₫"' },
    { header: 'ĐÃ THANH TOÁN (VND)', key: 'paidAmount', width: 22, align: 'right', numFmt: '#,##0 "₫"' },
    { header: 'CÒN NỢ (VND)', key: 'debtAmount', width: 20, align: 'right', numFmt: '#,##0 "₫"' },
    { header: 'TRẠNG THÁI', key: 'statusText', width: 18, align: 'center' },
  ];

  const formattedData = orders.map((o, idx) => {
    const total = Number(o.totalAmount || 0);
    const paid = Number(o.depositAmount || o.paidAmount || 0);
    const debt = Math.max(0, total - paid);

    let statusText = 'Đang xử lý';
    if (o.status === 'COMPLETED') statusText = 'Hoàn thành';
    else if (o.status === 'DELIVERED') statusText = 'Đã giao hàng';
    else if (o.status === 'CANCELLED') statusText = 'Đã hủy';
    else if (o.status === 'CONFIRMED') statusText = 'Đã xác nhận';

    return {
      stt: idx + 1,
      orderCode: o.orderId || o.code || '-',
      poNumber: o.poNumber || '-',
      customerName: o.customer?.name || o.customerName || 'Khách hàng B2B',
      orderDate: o.orderDate ? format(new Date(o.orderDate), 'dd/MM/yyyy') : '-',
      deliveryDate: o.deliveryDeadline ? format(new Date(o.deliveryDeadline), 'dd/MM/yyyy') : '-',
      totalAmount: total,
      paidAmount: paid,
      debtAmount: debt,
      statusText,
    };
  });

  const totalRev = summary?.totalRevenue ?? formattedData.reduce((s, r) => s + r.totalAmount, 0);
  const totalPaid = summary?.totalPaid ?? formattedData.reduce((s, r) => s + r.paidAmount, 0);
  const totalDebt = summary?.totalDebt ?? formattedData.reduce((s, r) => s + r.debtAmount, 0);

  await buildAndDownloadWorkbook({
    base: {
      title: 'BÁO CÁO DOANH THU BÁN HÀNG & THU HỒI CÔNG NỢ',
      periodText,
      creatorName,
      filename: `BaoCao_DoanhThu_BanHang_${format(new Date(), 'yyyyMMdd_HHmm')}`,
      sheetName: 'Doanh Thu Ban Hang'
    },
    columns,
    data: formattedData,
    summaryRow: {
      stt: 'TỔNG CỘNG',
      orderCode: `${formattedData.length} Đơn hàng`,
      customerName: '',
      totalAmount: totalRev,
      paidAmount: totalPaid,
      debtAmount: totalDebt,
    }
  });
}

/**
 * 2. BÁO CÁO TIẾN ĐỘ & HIỆU SUẤT LỆNH SẢN XUẤT (Production Progress & Scrap/Yield Report)
 */
export async function exportProductionReport(params: {
  periodText?: string;
  creatorName?: string;
  pos: any[];
  summary?: {
    totalPlan: number;
    totalProduced: number;
    totalWaste: number;
  };
}) {
  const { periodText, creatorName, pos, summary } = params;

  const columns: ReportColumnConfig[] = [
    { header: 'STT', key: 'stt', width: 8, align: 'center' },
    { header: 'MÃ LỆNH SX', key: 'poCode', width: 18, align: 'center' },
    { header: 'MÃ ĐƠN HÀNG', key: 'salesOrderCode', width: 16, align: 'center' },
    { header: 'TÊN SẢN PHẨM / BAO BÌ', key: 'productName', width: 32, align: 'left' },
    { header: 'KHÁCH HÀNG', key: 'customerName', width: 26, align: 'left' },
    { header: 'KẾ HOẠCH (PCS)', key: 'qtyPlan', width: 16, align: 'right', numFmt: '#,##0' },
    { header: 'ĐẠT OK (PCS)', key: 'qtyProduced', width: 16, align: 'right', numFmt: '#,##0' },
    { header: 'HỎNG NG (PCS)', key: 'qtyWaste', width: 16, align: 'right', numFmt: '#,##0' },
    { header: 'TỶ LỆ HAO HỤT (%)', key: 'wastagePercent', width: 18, align: 'right', numFmt: '0.0"%"' },
    { header: 'NĂNG SUẤT YIELD (%)', key: 'yieldRate', width: 18, align: 'right', numFmt: '0.0"%"' },
    { header: 'TRẠNG THÁI', key: 'statusText', width: 18, align: 'center' },
  ];

  const formattedData = pos.map((po, idx) => {
    const qtyPlan = Number(po.qtyPlan || po.quantity || 1000);
    const qtyProduced = Number(po.qtyProduced || po.qtyOk || 0);
    const qtyWaste = Number(po.qtyWaste || po.qtyNg || 0);
    const totalOut = qtyProduced + qtyWaste;
    const yieldRate = totalOut > 0 ? (qtyProduced / totalOut) * 100 : (qtyPlan > 0 ? (qtyProduced / qtyPlan) * 100 : 100);
    const wastagePercent = totalOut > 0 ? (qtyWaste / totalOut) * 100 : (qtyPlan > 0 ? (qtyWaste / qtyPlan) * 100 : 0);

    let statusText = 'Đang lập KH';
    if (po.status === 'COMPLETED') statusText = 'Đã hoàn thành';
    else if (po.status === 'IN_PROGRESS' || po.status === 'RUNNING') statusText = 'Đang sản xuất';
    else if (po.status === 'CANCELLED') statusText = 'Đã hủy';
    else if (po.status === 'PLANNED') statusText = 'Đã lên lịch';

    return {
      stt: idx + 1,
      poCode: po.orderId || po.poCode || po.lsxId || po.code || '-',
      salesOrderCode: po.salesOrder?.orderId || po.salesOrderId || '-',
      productName: po.productName || po.product?.name || 'Bao bì Carton/Hộp',
      customerName: po.customer?.name || po.customerName || 'Khách hàng APV',
      qtyPlan,
      qtyProduced,
      qtyWaste,
      wastagePercent: Math.round(wastagePercent * 10) / 10,
      yieldRate: Math.round(yieldRate * 10) / 10,
      statusText,
    };
  });

  const totalPlan = summary?.totalPlan ?? formattedData.reduce((s, r) => s + r.qtyPlan, 0);
  const totalProduced = summary?.totalProduced ?? formattedData.reduce((s, r) => s + r.qtyProduced, 0);
  const totalWaste = summary?.totalWaste ?? formattedData.reduce((s, r) => s + r.qtyWaste, 0);
  const avgYield = totalPlan > 0 ? Math.round((totalProduced / totalPlan) * 1000) / 10 : 100;
  const avgWastage = totalPlan > 0 ? Math.round((totalWaste / totalPlan) * 1000) / 10 : 0;

  await buildAndDownloadWorkbook({
    base: {
      title: 'BÁO CÁO TIẾN ĐỘ & HIỆU SUẤT LỆNH SẢN XUẤT',
      periodText,
      creatorName,
      filename: `BaoCao_TienDo_SanXuat_${format(new Date(), 'yyyyMMdd_HHmm')}`,
      sheetName: 'Tien Do San Xuat'
    },
    columns,
    data: formattedData,
    summaryRow: {
      stt: 'TỔNG CỘNG',
      poCode: `${formattedData.length} Lệnh SX`,
      qtyPlan: totalPlan,
      qtyProduced: totalProduced,
      qtyWaste: totalWaste,
      wastagePercent: avgWastage,
      yieldRate: avgYield,
    }
  });
}

/**
 * 3. BÁO CÁO SỔ KHO NHẬP - XUẤT - TỒN (Inventory NXT Report)
 */
export async function exportInventoryNXTReport(params: {
  periodText?: string;
  warehouseName?: string;
  creatorName?: string;
  items: any[];
}) {
  const { periodText, warehouseName, creatorName, items } = params;

  const columns: ReportColumnConfig[] = [
    { header: 'STT', key: 'stt', width: 8, align: 'center' },
    { header: 'MÃ VẬT TƯ / THÀNH PHẨM', key: 'itemCode', width: 22, align: 'center' },
    { header: 'TÊN VẬT TƯ / QUY CÁCH HÀNG HÓA', key: 'itemName', width: 34, align: 'left' },
    { header: 'LOẠI KHO', key: 'warehouseType', width: 16, align: 'center' },
    { header: 'KHO', key: 'warehouseName', width: 24, align: 'left' },
    { header: 'ĐVT', key: 'unit', width: 10, align: 'center' },
    { header: 'TỒN ĐẦU KỲ', key: 'openingQty', width: 16, align: 'right', numFmt: '#,##0' },
    { header: 'NHẬP TRONG KỲ', key: 'inQty', width: 16, align: 'right', numFmt: '#,##0' },
    { header: 'XUẤT TRONG KỲ', key: 'outQty', width: 16, align: 'right', numFmt: '#,##0' },
    { header: 'TỒN CUỐI KỲ', key: 'closingQty', width: 16, align: 'right', numFmt: '#,##0' },
    { header: 'ĐƠN GIÁ BÌNH QUÂN', key: 'unitPrice', width: 20, align: 'right', numFmt: '#,##0 "₫"' },
    { header: 'THÀNH TIỀN TỒN CUỐI (VND)', key: 'closingAmount', width: 24, align: 'right', numFmt: '#,##0 "₫"' },
  ];

  const formattedData = items.map((item, idx) => {
    const opening = Number(item.openingQty || item.openingStock || 0);
    const inQty = Number(item.inQty || item.totalImport || 0);
    const outQty = Number(item.outQty || item.totalExport || 0);
    const closing = Number(item.closingQty || item.currentStock || (opening + inQty - outQty));
    const unitPrice = Number(item.unitPrice || item.material?.unitPrice || 0);
    const closingAmount = closing * unitPrice;

    return {
      stt: idx + 1,
      itemCode: item.itemCode || item.itemId || item.material?.materialId || item.material?.code || item.product?.productCode || '-',
      itemName: item.itemName || item.material?.name || item.product?.name || item.name || 'Vật tư',
      warehouseType: item.itemType || item.warehouseType || warehouseName || 'NVL',
      warehouseName: item.warehouseName || item.warehouse?.name || '',
      unit: item.unit || item.material?.unit || item.product?.unit || 'Tờ',
      openingQty: opening,
      inQty,
      outQty,
      closingQty: closing,
      unitPrice,
      closingAmount,
    };
  });

  const totalOpening = formattedData.reduce((s, r) => s + r.openingQty, 0);
  const totalIn = formattedData.reduce((s, r) => s + r.inQty, 0);
  const totalOut = formattedData.reduce((s, r) => s + r.outQty, 0);
  const totalClosing = formattedData.reduce((s, r) => s + r.closingQty, 0);
  const totalValuation = formattedData.reduce((s, r) => s + r.closingAmount, 0);

  await buildAndDownloadWorkbook({
    base: {
      title: `BÁO CÁO TỔNG HỢP NHẬP - XUẤT - TỒN (SỔ KHO ${warehouseName || 'DOANH NGHIỆP'})`,
      periodText,
      creatorName,
      filename: `BaoCao_NXT_${warehouseName || 'Kho'}_${format(new Date(), 'yyyyMMdd_HHmm')}`,
      sheetName: 'So Kho NXT'
    },
    columns,
    data: formattedData,
    summaryRow: {
      stt: 'TỔNG CỘNG',
      itemCode: `${formattedData.length} Mặt hàng`,
      openingQty: totalOpening,
      inQty: totalIn,
      outQty: totalOut,
      closingQty: totalClosing,
      closingAmount: totalValuation,
    }
  });
}

/**
 * 4. BÁO CÁO PHÂN TÍCH TUỔI NỢ KHÁCH HÀNG (Customer AR Aging Report)
 */
export async function exportARAgingReport(params: {
  asOfDate?: Date;
  creatorName?: string;
  agingData: any[];
  summary?: {
    totalOutstanding: number;
    totalCurrent: number;
    totalOverdue30: number;
    totalOverdue60: number;
    totalOverdue90: number;
  };
}) {
  const { asOfDate, creatorName, agingData, summary } = params;
  const asOf = asOfDate || new Date();

  const columns: ReportColumnConfig[] = [
    { header: 'STT', key: 'stt', width: 8, align: 'center' },
    { header: 'MÃ KHÁCH HÀNG', key: 'customerCode', width: 18, align: 'center' },
    { header: 'TÊN KHÁCH HÀNG', key: 'customerName', width: 34, align: 'left' },
    { header: 'TỔNG DƯ NỢ (VND)', key: 'totalDebt', width: 22, align: 'right', numFmt: '#,##0 "₫"' },
    { header: 'TRONG HẠN (0-30 NGÀY)', key: 'current', width: 22, align: 'right', numFmt: '#,##0 "₫"' },
    { header: 'QUÁ HẠN 31-60 NGÀY', key: 'overdue30', width: 22, align: 'right', numFmt: '#,##0 "₫"' },
    { header: 'QUÁ HẠN 61-90 NGÀY', key: 'overdue60', width: 22, align: 'right', numFmt: '#,##0 "₫"' },
    { header: 'QUÁ HẠN > 90 NGÀY', key: 'overdue90', width: 22, align: 'right', numFmt: '#,##0 "₫"' },
    { header: 'ĐÁNH GIÁ RỦI RO', key: 'riskLevel', width: 18, align: 'center' },
  ];

  const formattedData = agingData.map((item, idx) => {
    const total = Number(item.totalOutstanding || item.totalDebt || 0);
    const current = Number(item.current || 0);
    const o30 = Number(item.overdue30 || 0);
    const o60 = Number(item.overdue60 || 0);
    const o90 = Number(item.overdue90 || 0);

    let riskLevel = 'An toàn';
    if (o90 > 0) riskLevel = 'Nguy cơ xấu';
    else if (o60 > 0) riskLevel = 'Cảnh báo cao';
    else if (o30 > 0) riskLevel = 'Cần nhắc nhở';

    return {
      stt: idx + 1,
      customerCode: item.customerCode || item.customer?.code || item.customerId || '-',
      customerName: item.customerName || item.customer?.name || 'Khách hàng',
      totalDebt: total,
      current,
      overdue30: o30,
      overdue60: o60,
      overdue90: o90,
      riskLevel,
    };
  });

  const totalDebt = summary?.totalOutstanding ?? formattedData.reduce((s, r) => s + r.totalDebt, 0);
  const totalCurrent = summary?.totalCurrent ?? formattedData.reduce((s, r) => s + r.current, 0);
  const totalO30 = summary?.totalOverdue30 ?? formattedData.reduce((s, r) => s + r.overdue30, 0);
  const totalO60 = summary?.totalOverdue60 ?? formattedData.reduce((s, r) => s + r.overdue60, 0);
  const totalO90 = summary?.totalOverdue90 ?? formattedData.reduce((s, r) => s + r.overdue90, 0);

  await buildAndDownloadWorkbook({
    base: {
      title: 'BÁO CÁO TỔNG HỢP PHÂN TÍCH TUỔI NỢ KHÁCH HÀNG (AR AGING)',
      periodText: `Tính đến ngày: ${format(asOf, 'dd/MM/yyyy')}`,
      creatorName,
      filename: `BaoCao_TuoiNo_KhachHang_${format(asOf, 'yyyyMMdd_HHmm')}`,
      sheetName: 'Tuoi No Khach Hang'
    },
    columns,
    data: formattedData,
    summaryRow: {
      stt: 'TỔNG CỘNG',
      customerCode: `${formattedData.length} Khách hàng`,
      totalDebt,
      current: totalCurrent,
      overdue30: totalO30,
      overdue60: totalO60,
      overdue90: totalO90,
    }
  });
}
